/**
 * A publishable MediaStream with nobody in it.
 *
 * Written for wave 3's WHIP test, where the machine running the browser has no
 * camera, but it is not only a test seam. **This project has no consent flow and
 * its own `CLAUDE.md` says not to point it at minors.** A synthetic source is how
 * the whole stack gets exercised in an actual venue, on the actual wifi, over the
 * actual media plane, without filming anyone. That is the one configuration where
 * a gym test is unambiguously legal, so it belongs in the product rather than in
 * `scripts/`.
 *
 * It is deliberately NOT a still image. The mechanical gate scores motion and
 * shake, the scene gate decides whether the picture filter runs at all, and a
 * frozen frame exercises none of that: it would pass through the interesting half
 * of the pipeline without touching it, which is the same shape as a benchmark that
 * cannot fail.
 */

/** What a synthetic publisher offers, so a caller can stop it as well as start it. */
export interface SyntheticSource {
  stream: MediaStream;
  /** Stops the animation and the audio graph. A canvas track left running keeps a tab busy forever. */
  stop: () => void;
}

/**
 * A moving test pattern plus a quiet tone.
 *
 * Audio is a real oscillator at a low gain rather than silence, because the
 * speech gate's VAD in front of it is supposed to find no speech: silence and
 * "sound with no speech in it" are different inputs and only the second one
 * exercises the decision.
 */
export function syntheticSource(label: string, fps = 30): SyntheticSource {
  const canvas = document.createElement('canvas');
  canvas.width = 1280;
  canvas.height = 720;
  const ctx = canvas.getContext('2d')!;

  let frame = 0;
  /**
   * A timer, not `requestAnimationFrame`, and that is a measured choice.
   *
   * The first browser WHIP publish on 2026-08-29 ran with the pane backgrounded.
   * rAF stops in a hidden tab, so the canvas froze, no video frames were encoded,
   * and every outbound stat read zero while AUDIO kept flowing: MediaMTX showed
   * bytes still arriving at about 38kbps, which looks like a working publisher
   * until you notice the picture is one frame old. The mechanical gate then
   * refused the feed for `0fps` and `0x0`, which is correct and points at the
   * wrong thing.
   *
   * A timer is throttled in a background tab too, so this is a mitigation and not
   * a fix. It is the same failure a real phone has when its screen locks, which
   * is what `holdScreenAwake()` exists for, and the server treats silence as a
   * dead feed for the same reason.
   */
  let timer = 0;
  const draw = (): void => {
    frame++;
    const t = frame / fps;
    // A lit court rather than a dark one: the mechanical gate refuses a dark
    // picture, and a synthetic source that fails gate 0 tests nothing beyond it.
    ctx.fillStyle = '#6b6250';
    ctx.fillRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#d8d2c4';
    ctx.lineWidth = 6;
    ctx.strokeRect(80, 60, canvas.width - 160, canvas.height - 120);
    ctx.beginPath();
    ctx.arc(canvas.width / 2, canvas.height / 2, 110, 0, Math.PI * 2);
    ctx.stroke();
    // Something that actually moves, so motion and the scene gate have work.
    const x = canvas.width / 2 + Math.sin(t * 1.7) * 380;
    const y = canvas.height / 2 + Math.cos(t * 2.3) * 180;
    ctx.fillStyle = '#e07a2f';
    ctx.beginPath();
    ctx.arc(x, y, 34, 0, Math.PI * 2);
    ctx.fill();
    ctx.fillStyle = '#f2eee4';
    ctx.font = '38px system-ui, sans-serif';
    ctx.fillText(`SYNTHETIC  ${label}`, 110, 130);
    ctx.font = '24px system-ui, sans-serif';
    ctx.fillText('no camera, nobody filmed', 110, 168);

    // High-frequency detail, and it is load-bearing rather than decorative.
    //
    // Measured 2026-08-29 on the first browser WHIP publish: a clean synthetic
    // pattern encoded at about 550kbps against the mechanical gate's 600kbps
    // floor and was refused, correctly, while looking perfect on screen. Flat
    // colour and one moving ball give H.264 almost nothing to spend bits on, and
    // a real gym does not look like that. Without this the test source fails
    // gate 0 for a reason no operator could ever act on.
    ctx.globalAlpha = 0.06;
    for (let i = 0; i < 1400; i++) {
      ctx.fillStyle = (frame + i) % 2 ? '#ffffff' : '#000000';
      ctx.fillRect(
        (Math.sin(i * 12.9898 + t) * 43758.5453 % 1 + 1) / 2 * canvas.width,
        (Math.sin(i * 78.233 + t) * 43758.5453 % 1 + 1) / 2 * canvas.height,
        3, 3,
      );
    }
    ctx.globalAlpha = 1;
  };
  draw();
  timer = window.setInterval(draw, Math.round(1000 / fps));

  const stream = (canvas as HTMLCanvasElement & {
    captureStream: (fps?: number) => MediaStream;
  }).captureStream(fps);

  // An audio track has to exist: `publish()` offers whatever the stream carries,
  // and MediaMTX negotiates against that offer. A video-only publisher would
  // silently give the speech gate nothing to rule on, which reads downstream as
  // "no transcriber configured" rather than as a missing track.
  const audio = new AudioContext();
  const osc = audio.createOscillator();
  const gain = audio.createGain();
  osc.frequency.value = 220;
  gain.gain.value = 0.02;
  const dest = audio.createMediaStreamDestination();
  osc.connect(gain).connect(dest);
  osc.start();
  for (const track of dest.stream.getAudioTracks()) stream.addTrack(track);

  return {
    stream,
    stop: () => {
      window.clearInterval(timer);
      osc.stop();
      void audio.close();
      for (const track of stream.getTracks()) track.stop();
    },
  };
}
